import { useState } from "react";
import { Loader2, Save, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const FIELDS = [
  ["poNumber", "PO number"],
  ["poDate", "PO date"],
  ["branchCode", "Site / branch code"],
  ["branchName", "Site / branch name"],
  ["city", "City"],
  ["state", "State"],
  ["branchAddress", "Delivery address"],
  ["spocName", "Site contact name"],
  ["spocContact", "Site contact number"],
];

/** Teach the app how this customer's POs are laid out, then prove it on a sample. */
export default function ReadingProfile({ customer, onSaved }) {
  const reading = customer.reading || {};
  const [instructions, setInstructions] = useState(reading.instructions || "");
  const [hints, setHints] = useState(reading.hints || {});
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sample, setSample] = useState(null);
  const [read, setRead] = useState(null);
  const [fixed, setFixed] = useState({});
  const corrections = reading.corrections || [];

  const saveProfile = async () => {
    setBusy(true);
    try {
      await api.patch(`/customers/${customer.id}`, {
        reading: { ...reading, instructions, hints },
      });
      toast.success("Reading profile saved");
      await onSaved?.();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const test = async (file) => {
    if (!file) return;
    setSample(file.name);
    setTesting(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(`/customers/${customer.id}/reading/test`, form);
      setRead(data.extracted);
      setFixed({});
      toast.success("Read with this customer's profile. Correct anything it got wrong.");
    } catch (e) {
      toast.error(errText(e));
      setRead(null);
    } finally {
      setTesting(false);
    }
  };

  const learn = async () => {
    const payload = FIELDS.filter(([key]) => (fixed[key] ?? "") !== "" && fixed[key] !== read?.[key]).map(
      ([key]) => ({ field: key, wrong: read?.[key] ?? null, right: fixed[key] })
    );
    if (payload.length === 0) {
      toast.info("Nothing corrected yet");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/customers/${customer.id}/reading/corrections`, {
        corrections: payload,
        sample,
      });
      toast.success(`${payload.length} correction(s) learnt`);
      await onSaved?.();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const forget = async (c) => {
    const kept = corrections.filter((x) => !(x.field === c.field && x.wrong === c.wrong));
    try {
      await api.post(`/customers/${customer.id}/reading/corrections`, {
        corrections: kept,
        replace: true,
      });
      toast.success("Correction removed");
      await onSaved?.();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <div className="panel p-3 sm:p-4" data-testid="reading-profile-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-medium">How to read {customer.name}'s POs</h2>
        <Button size="sm" onClick={saveProfile} disabled={busy} data-testid="reading-save-button">
          <Save className="mr-2 h-4 w-4" />
          Save the profile
        </Button>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Describe this customer's PO format and say where each field sits. The reader uses this profile, plus every
        correction below, whenever a PO of this customer is uploaded.
      </p>

      <div className="mt-3">
        <Label className="field-label">Reading instructions</Label>
        <Textarea
          rows={5}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder="For example: the delivery site sits under 'Ship to', never the billing block. Item rows start after the words 'Description of goods'."
          data-testid="reading-instructions-input"
        />
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {FIELDS.map(([key, label]) => (
          <div key={key}>
            <Label className="field-label">{label} — where to find it</Label>
            <Input
              value={hints[key] || ""}
              onChange={(e) => setHints((h) => ({ ...h, [key]: e.target.value }))}
              className="h-9"
              data-testid={`reading-hint-${key}`}
            />
          </div>
        ))}
      </div>

      <div className="mt-4 border-t border-border pt-3">
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-input px-3 py-2 text-sm hover:bg-secondary">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {testing ? "Reading the sample…" : "Upload a sample PO"}
            <input
              type="file"
              accept=".pdf"
              className="hidden"
              data-testid="reading-sample-input"
              onChange={(e) => test(e.target.files?.[0])}
            />
          </label>
          {sample && <span className="text-xs text-muted-foreground">{sample}</span>}
        </div>

        {read && (
          <div className="mt-3" data-testid="reading-sample-result">
            <p className="text-xs text-muted-foreground">
              What the reader took from the sample. Correct anything wrong and save it as a lesson — nothing here
              creates a PO.
            </p>
            <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {FIELDS.map(([key, label]) => (
                <div key={key}>
                  <Label className="field-label">{label}</Label>
                  <Input
                    value={fixed[key] ?? (read[key] ?? "")}
                    onChange={(e) => setFixed((f) => ({ ...f, [key]: e.target.value }))}
                    className="h-9"
                    data-testid={`reading-read-${key}`}
                  />
                </div>
              ))}
            </div>
            {(read.lines || []).length > 0 && (
              <ul className="mt-3 space-y-1 text-xs text-muted-foreground" data-testid="reading-read-lines">
                {read.lines.map((l, i) => (
                  <li key={i}>
                    {l.itemType || "—"} · {l.product || "—"} · qty {l.qty ?? "—"} · rate {l.rate ?? "—"}
                  </li>
                ))}
              </ul>
            )}
            <Button size="sm" className="mt-3" onClick={learn} disabled={busy} data-testid="reading-learn-button">
              Save these corrections
            </Button>
          </div>
        )}
      </div>

      <div className="mt-4 border-t border-border pt-3">
        <h3 className="text-xs font-medium">Lessons on record ({corrections.length})</h3>
        {corrections.length === 0 ? (
          <p className="mt-1 text-xs text-muted-foreground">Nothing learnt yet.</p>
        ) : (
          <ul className="mt-2 space-y-1">
            {corrections.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-xs" data-testid={`reading-correction-${i}`}>
                <span className="font-mono text-primary">{c.field}</span>
                <span className="text-muted-foreground line-through">{String(c.wrong ?? "—")}</span>
                <span>→ {String(c.right)}</span>
                <button
                  onClick={() => forget(c)}
                  className="ml-auto text-muted-foreground hover:text-red-600"
                  data-testid={`reading-correction-remove-${i}`}
                  aria-label="Remove this lesson"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
